"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import {
  Trash2,
  PackageOpen,
  BicepsFlexed,
  Truck,
  Warehouse,
  Armchair,
  WashingMachine,
  Monitor,
  Recycle,
  ShoppingBag,
  Hammer,
  Send,
  Wrench,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { isHrefHidden } from "@/lib/booking/hidden-services";

/* ------------------------------------------------------------------ */
/*  Slug helper                                                         */
/* ------------------------------------------------------------------ */

function labelToSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/ö/g, "o")
    .replace(/ä/g, "a")
    .replace(/å/g, "a")
    .replace(/é/g, "e")
    .replace(/&/g, "")
    .replace(/\s+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
}

/* ------------------------------------------------------------------ */
/*  Data                                                                */
/* ------------------------------------------------------------------ */

interface ServiceItem {
  label: string;
  icon: LucideIcon;
  iconColor: string;   // hex for icon
  bgTint: string;      // hex for light bg tint
  borderColor: string;  // hex for border
  subItems: string[];
}

const services: ServiceItem[] = [
  {
    label: "Bortforsling",
    icon: Trash2,
    iconColor: "#f89544",
    bgTint: "#FFF7ED",
    borderColor: "#FDBA74",
    subItems: [
      "Bortforsling",
      "Bortforsling Möbler",
      "Bortforsling Vitvaror",
      "Bortforsling Dödsbo",
    ],
  },
  {
    label: "Tömningar",
    icon: PackageOpen,
    iconColor: "#3b82f6",
    bgTint: "#EFF6FF",
    borderColor: "#93C5FD",
    subItems: [
      "Tömma Källare",
      "Tömma Förråd",
      "Tömma Lägenhet",
      "Tömma Dödsbo",
      "Tömma Kontor",
    ],
  },
  {
    label: "Bärhjälp",
    icon: BicepsFlexed,
    iconColor: "#22c55e",
    bgTint: "#F0FDF4",
    borderColor: "#86EFAC",
    subItems: [
      "Bärhjälp",
      "Bärhjälp Flytt",
    ],
  },
  {
    label: "Flytthjälp",
    icon: Truck,
    iconColor: "#8b5cf6",
    bgTint: "#F5F3FF",
    borderColor: "#C4B5FD",
    subItems: [
      "Flytthjälp",
      "Bohagsflytt",
      "Flytt Företag",
      "Studentflytt",
      "Flytta Möbler",
    ],
  },
  {
    label: "Transport förråd",
    icon: Warehouse,
    iconColor: "#0d9488",
    bgTint: "#F0FDFA",
    borderColor: "#5EEAD4",
    subItems: [
      "Transport Förråd",
      "Transport Pelican",
      "Transport Shurgard",
      "Transport Green Storage",
      "Transport Storage365",
    ],
  },
  {
    label: "Transport möbler",
    icon: Armchair,
    iconColor: "#e11d48",
    bgTint: "#FFF1F2",
    borderColor: "#FDA4AF",
    subItems: [
      "Transport Möbler",
      "Transport Soffa",
      "Transport Säng",
    ],
  },
  {
    label: "Transport vitvaror",
    icon: WashingMachine,
    iconColor: "#f59e0b",
    bgTint: "#FFFBEB",
    borderColor: "#FCD34D",
    subItems: [
      "Transport Vitvaror",
      "Transport Diskmaskin",
      "Transport Tvättmaskin & Torktumlare",
      "Transport Kyl & Frys",
    ],
  },
  {
    label: "Transporthjälp",
    icon: Monitor,
    iconColor: "#6366f1",
    bgTint: "#EEF2FF",
    borderColor: "#A5B4FC",
    subItems: [
      "Transporthjälp",
      "Transport Elektronik",
      "Transport TV",
    ],
  },
  {
    label: "Transport återbruk",
    icon: Recycle,
    iconColor: "#16a34a",
    bgTint: "#F0FDF4",
    borderColor: "#86EFAC",
    subItems: [
      "Transport Återbruk",
      "Transport Blocket",
      "Transport Facebook Marketplace",
      "Transport Tradera",
      "Transport Myrorna",
      "Transport Erikshjälpen",
      "Transport Röda Korset",
      "Transport Stadsmissionen",
    ],
  },
  {
    label: "Hemleverans butik",
    icon: ShoppingBag,
    iconColor: "#ec4899",
    bgTint: "#FDF2F8",
    borderColor: "#F9A8D4",
    subItems: [
      "Hemleverans Butik",
      "Hemleverans Elgiganten",
      "Hemleverans Power",
      "Hemleverans IKEA",
      "Hemleverans JYSK",
      "Hemleverans Rusta",
      "Hemleverans Jula",
      "Hemleverans Mio",
      "Hemleverans Bauhaus",
      "Hemleverans K-Rauta",
      "Hemleverans Byggmax",
      "Hemleverans Biltema",
    ],
  },
  {
    label: "Hemleverans auktion",
    icon: Hammer,
    iconColor: "#ef4444",
    bgTint: "#FEF2F2",
    borderColor: "#FCA5A5",
    subItems: [
      "Hemleverans Auktion",
      "Hemleverans Ps Auktion",
      "Hemleverans Budi",
      "Hemleverans Stockholms auktionsverk",
      "Hemleverans Bukowskis",
      "Hemleverans Uppsala auktionskammare",
      "Hemleverans Auctionet",
      "Hemleverans Klaravik",
    ],
  },
  {
    label: "Bud",
    icon: Send,
    iconColor: "#0ea5e9",
    bgTint: "#F0F9FF",
    borderColor: "#7DD3FC",
    subItems: [
      "Bud",
      "Budbil",
      "Cykelbud",
    ],
  },
  {
    label: "Möbelmontering",
    icon: Wrench,
    iconColor: "#8b5cf6",
    bgTint: "#F5F3FF",
    borderColor: "#C4B5FD",
    subItems: [
      "Möbelmontering",
    ],
  },
];

const COLS = 4;

/* ------------------------------------------------------------------ */
/*  Card                                                                */
/* ------------------------------------------------------------------ */

function MenuCard({
  item,
  isActive,
  onClick,
}: {
  item: ServiceItem;
  isActive: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;
  const showColor = isActive || hovered;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderColor: showColor ? item.borderColor : "#F3F4F6",
        backgroundColor: isActive ? item.bgTint : hovered ? item.bgTint : "#FFFFFF",
      }}
      className="flex items-center gap-2 sm:gap-4 px-2.5 sm:px-5 py-2 sm:py-3 rounded-lg sm:rounded-xl border-2 transition-all duration-200 cursor-pointer text-left w-full"
    >
      <div
        className="flex-shrink-0 w-6 h-6 sm:w-9 sm:h-9 rounded-md sm:rounded-lg flex items-center justify-center"
        style={{ backgroundColor: item.bgTint }}
      >
        <Icon
          className="w-3 h-3 sm:w-[18px] sm:h-[18px]"
          style={{ color: item.iconColor }}
        />
      </div>
      <span
        className="text-xs sm:text-[15px] font-semibold flex-1 leading-tight transition-colors duration-200 break-words hyphens-auto"
        style={{ color: showColor ? item.iconColor : "#000000" }}
      >
        {item.label}
      </span>
      {isActive ? (
        <ChevronDown
          className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
          style={{ color: item.iconColor }}
        />
      ) : (
        <ChevronRight
          className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 transition-colors duration-200"
          style={{ color: hovered ? item.iconColor : "#D1D5DB" }}
        />
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-item with hover                                                 */
/* ------------------------------------------------------------------ */

function SubItem({
  label,
  href,
  hoverColor,
  hoverBg,
}: {
  label: string;
  href: string;
  hoverColor: string;
  hoverBg: string;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      href={href}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        color: hovered ? hoverColor : "#6B7280",
        backgroundColor: hovered ? hoverBg : "transparent",
      }}
      className="text-sm transition-all duration-150 cursor-pointer rounded-lg px-3 py-1.5 -mx-3"
    >
      {label}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                      */
/* ------------------------------------------------------------------ */

import GuidePreviewSection from "@/components/guides/GuidePreviewSection";
import type { Locale } from "./templates/ServicePageTemplate";

const servicesEn: ServiceItem[] = [
  { label: "Junk Removal", icon: Trash2, iconColor: "#f89544", bgTint: "#FFF7ED", borderColor: "#FDBA74", subItems: ["Junk Removal", "Furniture Removal", "Appliance Removal", "Estate Clearance"] },
  { label: "Property Clearance", icon: PackageOpen, iconColor: "#3b82f6", bgTint: "#EFF6FF", borderColor: "#93C5FD", subItems: ["Basement Clearance", "Storage Unit Clearance", "Apartment Clearance", "House Clearance", "Office Clearance"] },
  { label: "Carrying Help", icon: BicepsFlexed, iconColor: "#22c55e", bgTint: "#F0FDF4", borderColor: "#86EFAC", subItems: ["Carrying Help", "Carrying help move"] },
  { label: "Moving Help", icon: Truck, iconColor: "#8b5cf6", bgTint: "#F5F3FF", borderColor: "#C4B5FD", subItems: ["Moving Help", "Household moving", "Corporate moving", "Student moving", "Furniture moving"] },
  { label: "Storage Transport", icon: Warehouse, iconColor: "#0d9488", bgTint: "#F0FDFA", borderColor: "#5EEAD4", subItems: ["Storage Transport", "Transport Pelican", "Transport Shurgard", "Transport Green Storage", "Transport Storage365"] },
  { label: "Furniture Transport", icon: Armchair, iconColor: "#e11d48", bgTint: "#FFF1F2", borderColor: "#FDA4AF", subItems: ["Furniture Transport", "Sofa transport", "Bed transport"] },
  { label: "Appliance Transport", icon: WashingMachine, iconColor: "#f59e0b", bgTint: "#FFFBEB", borderColor: "#FCD34D", subItems: ["Appliance Transport", "Dishwasher transport", "Washer & dryer transport", "Fridge & freezer transport"] },
  { label: "Transport Help", icon: Monitor, iconColor: "#6366f1", bgTint: "#EEF2FF", borderColor: "#A5B4FC", subItems: ["Transport Help", "Electronics Transport", "TV transport"] },
  { label: "Recycling Transport", icon: Recycle, iconColor: "#16a34a", bgTint: "#F0FDF4", borderColor: "#86EFAC", subItems: ["Second Hand Transport", "Blocket transport", "Facebook marketplace transport", "Tradera transport", "Myrorna transport", "Erikshjälpen transport", "Red Cross transport", "Stadsmissionen transport"] },
  { label: "Store Delivery", icon: ShoppingBag, iconColor: "#ec4899", bgTint: "#FDF2F8", borderColor: "#F9A8D4", subItems: ["Store Delivery", "Elgiganten delivery", "Power delivery", "IKEA delivery", "JYSK delivery", "Rusta delivery", "Jula delivery", "Mio delivery", "Bauhaus delivery", "K-Rauta delivery", "Byggmax delivery", "Biltema delivery"] },
  { label: "Auction Delivery", icon: Hammer, iconColor: "#ef4444", bgTint: "#FEF2F2", borderColor: "#FCA5A5", subItems: ["Auction Delivery", "Ps Auction Delivery", "Budi Auction Delivery", "Stockholms auktionsverk delivery", "Bukowskis delivery", "Uppsala auktionskammare delivery", "Auctionet delivery", "Klaravik delivery"] },
  { label: "Courier Service", icon: Send, iconColor: "#0ea5e9", bgTint: "#F0F9FF", borderColor: "#7DD3FC", subItems: ["Courier Service", "Delivery Van", "Bicycle Courier"] },
  { label: "Furniture Assembly", icon: Wrench, iconColor: "#8b5cf6", bgTint: "#F5F3FF", borderColor: "#C4B5FD", subItems: ["Furniture Assembly"] },
];

export default function ServicesMenu({ locale = "sv" }: { locale?: Locale }) {
  // Pausade tjänster (lib/booking/hidden-services.ts) visas inte.
  const localServices = (locale === "en" ? servicesEn : services).filter(
    (item) => !isHrefHidden(`${locale === "en" ? "/en/" : "/"}${labelToSlug(item.label)}`),
  );
  const menuHeading = locale === "en" ? "The youth are ready for:" : "Ungdomarna står redo för:";
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [cols, setCols] = useState(COLS);

  useEffect(() => {
    const update = () => setCols(window.innerWidth >= 1024 ? 4 : 2);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const rows: ServiceItem[][] = [];
  for (let i = 0; i < localServices.length; i += cols) {
    rows.push(localServices.slice(i, i + cols));
  }

  function toggle(globalIndex: number) {
    setActiveIndex((prev) => (prev === globalIndex ? null : globalIndex));
  }

  return (
    <>
    <section id="alla-tjanster" className="py-8 sm:pt-10 sm:pb-24 bg-white scroll-mt-8">
      <div className="mx-auto max-w-screen-2xl px-6 xl:px-14">
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark mb-10 sm:mb-14"
        >
          {menuHeading}
        </h2>

        <div className="space-y-0">
          {rows.map((row, rowIdx) => {
            const rowStart = rowIdx * cols;
            const activeInRow =
              activeIndex !== null &&
              activeIndex >= rowStart &&
              activeIndex < rowStart + cols
                ? activeIndex
                : null;
            const activeItem =
              activeInRow !== null ? localServices[activeInRow] : null;

            return (
              <div key={rowIdx}>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                  {row.map((item, colIdx) => {
                    const globalIdx = rowStart + colIdx;
                    return (
                      <MenuCard
                        key={item.label}
                        item={item}
                        isActive={activeIndex === globalIdx}
                        onClick={() => toggle(globalIdx)}
                      />
                    );
                  })}
                </div>

                {activeItem && (
                  <div
                    key={`expanded-${activeInRow}`}
                    className="overflow-hidden animate-[services-expand_0.25s_ease-in-out_both]"
                  >
                    <div className="pt-4 pb-5 sm:pt-5 sm:pb-7 px-1">
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-2.5">
                        {activeItem.subItems.map((sub) => (
                          <SubItem
                            key={sub}
                            label={sub}
                            href={`${locale === "en" ? "/en" : ""}/${labelToSlug(sub)}`}
                            hoverColor={activeItem.iconColor}
                            hoverBg={activeItem.bgTint}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {rowIdx < rows.length - 1 && <div className="h-3 sm:h-4" />}
              </div>
            );
          })}
        </div>
      </div>
    </section>

    <GuidePreviewSection locale={locale} />
    </>
  );
}
